import React, { useState, useEffect } from 'react';
import { Plug, Plus, Trash2, CheckCircle, RefreshCw, Users, Share2 } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { platformConnectionsApi, oauthApi, teamsApi } from '../lib/api';
import type { PlatformConnection, PlatformInfo } from '../types/vault';
import type { OAuthConnection, Team } from '../lib/api';

export default function PlatformConnections() {
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [platforms, setPlatforms] = useState<PlatformInfo[]>([]);
  const [oauthConnections, setOauthConnections] = useState<OAuthConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedOauthId, setSelectedOauthId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [creating, setCreating] = useState(false);
  const [validating, setValidating] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [sharingConnId, setSharingConnId] = useState<string | null>(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [connRes, platRes, oauthRes, teamsRes] = await Promise.all([
        platformConnectionsApi.list(),
        platformConnectionsApi.listPlatforms(),
        oauthApi.getConnections(),
        teamsApi.list(),
      ]);
      if (connRes.success && connRes.data) setConnections(connRes.data.connections);
      if (platRes.success && platRes.data) setPlatforms(platRes.data.platforms);
      if (oauthRes.success && oauthRes.data) {
        const data = oauthRes.data as unknown as { connections?: OAuthConnection[] };
        setOauthConnections(data.connections || []);
      }
      if (teamsRes.success && teamsRes.data) setTeams(teamsRes.data.teams);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedPlatform) return;
    const platform = platforms.find((p) => p.id === selectedPlatform);
    if (!platform) return;

    if (platform.authType === 'oauth' && !selectedOauthId) return;
    if (platform.authType === 'api_key' && !apiKey) return;
    if (selectedPlatform === 'clickfunnels' && !subdomain) return;

    setCreating(true);
    try {
      const res = await platformConnectionsApi.create(selectedPlatform, {
        oauthTokenId: platform.authType === 'oauth' ? selectedOauthId : undefined,
        apiKey: platform.authType === 'api_key' ? apiKey : undefined,
        subdomain: selectedPlatform === 'clickfunnels' ? subdomain : undefined,
        displayName: displayName || undefined,
      });
      if (res.success) {
        setShowCreateModal(false);
        setSelectedPlatform('');
        setSelectedOauthId('');
        setApiKey('');
        setSubdomain('');
        setDisplayName('');
        loadData();
      } else {
        setError(res.message || 'Failed to create connection');
      }
    } catch {
      setError('Failed to create connection');
    } finally {
      setCreating(false);
    }
  };

  const handleValidate = async (id: string) => {
    setValidating(id);
    try {
      const res = await platformConnectionsApi.validate(id);
      if (res.success && res.data) {
        if (res.data.valid) {
          loadData();
        } else {
          setError(`Validation failed: ${res.data.error}`);
        }
      }
    } catch {
      setError('Validation failed');
    } finally {
      setValidating(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this connection?')) return;
    try {
      const res = await platformConnectionsApi.delete(id);
      if (res.success) loadData();
    } catch {
      setError('Failed to delete');
    }
  };

  const handleShare = async (connectionId: string, teamId: string) => {
    if (!teamId) return;
    setShareBusy(true);
    try {
      const res = await teamsApi.shareConnection(teamId, connectionId);
      if (res.success) {
        setSharingConnId(null);
        loadData();
      } else {
        setError(res.message || 'Failed to share connection');
      }
    } catch {
      setError('Failed to share connection');
    } finally {
      setShareBusy(false);
    }
  };

  const handleUnshare = async (conn: PlatformConnection) => {
    if (!conn.teamId) return;
    if (!confirm('Stop sharing this connection with the team?')) return;
    setShareBusy(true);
    try {
      const res = await teamsApi.unshareConnection(conn.teamId, conn.id);
      if (res.success) {
        loadData();
      } else {
        setError(res.message || 'Failed to unshare connection');
      }
    } catch {
      setError('Failed to unshare connection');
    } finally {
      setShareBusy(false);
    }
  };

  const teamNameFor = (teamId: string | null | undefined): string => {
    if (!teamId) return 'Shared';
    return teams.find((t) => t.id === teamId)?.name || 'Shared';
  };

  const selectedPlatformInfo = platforms.find((p) => p.id === selectedPlatform);

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Vault"
          title="Platform Connections"
          description="Connect your marketing platforms to enable backups"
          actions={
            <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
              Add Connection
            </Button>
          }
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : connections.length === 0 ? (
          <EmptyState
            icon={Plug}
            title="No connections yet"
            description="Connect a platform to start backing up your data."
            action={
              <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
                Add Your First Connection
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connections.map((conn) => {
              const ownsConnection = conn.isOwner !== false;
              return (
                <Card key={conn.id} padded>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display font-semibold text-ink">{conn.displayName}</h3>
                      <p className="text-sm text-ink-3 capitalize">{conn.platform}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <Badge
                        tone={conn.status === 'active' ? 'success' : conn.status === 'error' ? 'danger' : 'neutral'}
                        dot
                      >
                        {conn.status}
                      </Badge>
                      {conn.teamId && (
                        <Badge tone="accent" className="max-w-[160px]">
                          <Users className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{teamNameFor(conn.teamId)}</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                  {conn.workspaceName && (
                    <p className="text-sm text-ink-2 mt-2">Workspace: {conn.workspaceName}</p>
                  )}
                  {conn.lastValidatedAt && (
                    <p className="text-xs text-ink-3 mt-1 font-mono">
                      Last validated: {new Date(conn.lastValidatedAt).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-line-2">
                    <Button
                      variant="ghost"
                      icon={validating === conn.id ? undefined : CheckCircle}
                      onClick={() => handleValidate(conn.id)}
                      disabled={validating === conn.id}
                      className="!h-8 text-sm"
                    >
                      {validating === conn.id && <RefreshCw className="w-3 h-3 animate-spin" />}
                      Validate
                    </Button>
                    {ownsConnection && !conn.teamId && teams.length > 0 && (
                      sharingConnId === conn.id ? (
                        <select
                          autoFocus
                          className="ds-input !h-8 !py-0 text-sm w-auto"
                          disabled={shareBusy}
                          defaultValue=""
                          onChange={(e) => handleShare(conn.id, e.target.value)}
                          onBlur={() => setSharingConnId(null)}
                        >
                          <option value="">Select team...</option>
                          {teams.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      ) : (
                        <Button
                          variant="ghost"
                          icon={Share2}
                          onClick={() => setSharingConnId(conn.id)}
                          className="!h-8 text-sm"
                        >
                          Share
                        </Button>
                      )
                    )}
                    {ownsConnection && conn.teamId && (
                      <Button
                        variant="ghost"
                        icon={Share2}
                        onClick={() => handleUnshare(conn)}
                        disabled={shareBusy}
                        className="!h-8 text-sm"
                      >
                        Unshare
                      </Button>
                    )}
                    {ownsConnection && (
                      <Button
                        variant="danger"
                        icon={Trash2}
                        onClick={() => handleDelete(conn.id)}
                        className="!h-8 text-sm"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Add Platform Connection</h2>

              <div className="space-y-4">
                <div>
                  <label className="ds-label">Platform</label>
                  <select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    className="ds-input"
                  >
                    <option value="">Select a platform...</option>
                    {platforms.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.authType})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ds-label">Display Name (optional)</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="My ClickFunnels Account"
                    className="ds-input"
                  />
                </div>

                {selectedPlatformInfo?.authType === 'oauth' && (
                  <div>
                    <label className="ds-label">OAuth Connection</label>
                    <select
                      value={selectedOauthId}
                      onChange={(e) => setSelectedOauthId(e.target.value)}
                      className="ds-input"
                    >
                      <option value="">Select an OAuth connection...</option>
                      {oauthConnections.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.connectionName || o.workspaceName || o.id}
                        </option>
                      ))}
                    </select>
                    {oauthConnections.length === 0 && (
                      <p className="text-sm text-warning mt-1">
                        No OAuth connections found. Please connect via Settings first.
                      </p>
                    )}
                  </div>
                )}

                {selectedPlatformInfo?.authType === 'api_key' && (
                  <div>
                    <label className="ds-label">API Token</label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={`Enter ${selectedPlatformInfo.name} API token`}
                      className="ds-input"
                    />
                    <p className="text-xs text-ink-3 mt-1">
                      {selectedPlatform === 'clickfunnels'
                        ? 'You can find your API token in ClickFunnels under Account Settings > API Tokens.'
                        : 'Paste your platform API key or token here.'}
                    </p>
                  </div>
                )}

                {selectedPlatform === 'clickfunnels' && (
                  <div>
                    <label className="ds-label">Workspace Subdomain</label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={subdomain}
                        onChange={(e) => setSubdomain(e.target.value.toLowerCase().trim())}
                        placeholder="mycompany"
                        className="ds-input flex-1 !rounded-r-none"
                      />
                      <span className="bg-surface-2 border border-l-0 border-line rounded-r-[11px] px-3 py-2 text-sm text-ink-3 font-mono">
                        .myclickfunnels.com
                      </span>
                    </div>
                    <p className="text-xs text-ink-3 mt-1">
                      The subdomain from your ClickFunnels workspace URL.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={
                    creating ||
                    !selectedPlatform ||
                    (selectedPlatformInfo?.authType === 'oauth' && !selectedOauthId) ||
                    (selectedPlatformInfo?.authType === 'api_key' && !apiKey) ||
                    (selectedPlatform === 'clickfunnels' && !subdomain)
                  }
                >
                  {creating ? 'Creating...' : 'Create Connection'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
